import { useCallback, useEffect, useRef, useState } from "react";
import api from "../utils/axios";
import { PagedResult } from "../api/client-reports/types";

const emptyPage = {
	items: [],
	totalCount: 0,
	pageSize: 0,
	currentPage: 1,
	totalPages: 0,
};

// Lists go out as repeated keys (cityIds=10&cityIds=11), the form the API binds; axios's default
// would send cityIds[]=10, which the API ignores.
const paramsSerializer = { indexes: null };

// An API from before paging answers with the whole array; treat that as a failed request rather
// than render it as a page. Deploy the API first, then the web.
const readPage = <T>(url: string, data: any): PagedResult<T> => {
	if (!Array.isArray(data?.items)) {
		throw new Error(`${url} did not answer with a page`);
	}
	return data;
};

/**
 * One page of a list endpoint's opt-in paged mode: the API returns a page when the request carries
 * `currentPage` / `pageSize` (see the API's docs/api-documentations/client/Opt_In_Pagination.md).
 *
 * Fetches whenever `params` changes, so pass a memoised object; pass null for a filter that can
 * match nothing, to show an empty list without a request. A response that arrives after a newer
 * request is ignored. `reload()` refetches the page on screen, e.g. after an action; it reads the
 * latest params, so a handler that awaited a request while the user changed page reloads the new
 * page, not the old one. When rows leave the list and the page is past the end, `setCurrentPage`
 * is called with the new last page.
 */
export function usePagedList<T>(
	url: string,
	params: Record<string, unknown> | null,
	setCurrentPage: (page: number) => void
) {
	const [page, setPage] = useState<PagedResult<T>>(emptyPage);
	const requestRef = useRef(0);
	const paramsRef = useRef(params);

	const reload = useCallback(async () => {
		const requestId = ++requestRef.current;
		const currentParams = paramsRef.current;
		if (!currentParams) {
			setPage(emptyPage);
			return;
		}
		try {
			const response = await api.get(url, {
				params: currentParams,
				paramsSerializer,
			});
			const data = readPage<T>(url, response.data);
			if (requestId === requestRef.current) setPage(data);
		} catch (error) {
			console.log(error);
			if (requestId === requestRef.current) setPage(emptyPage);
		}
	}, [url]);

	useEffect(() => {
		paramsRef.current = params;
		reload();
	}, [reload, params]);

	useEffect(() => {
		const lastPage = Math.max(page.totalPages, 1);
		if (page.currentPage > lastPage) setCurrentPage(lastPage);
	}, [page, setCurrentPage]);

	// "Showing firstRow - lastRow of totalCount" for the pager, from the rows actually shown.
	const firstRow = page.items.length
		? (page.currentPage - 1) * page.pageSize + 1
		: 0;
	const lastRow = firstRow ? firstRow + page.items.length - 1 : 0;

	return { ...page, firstRow, lastRow, reload };
}

/** Every row matching `params`, read 500 at a time: what "export all" needs. */
export async function fetchAllPages<T>(
	url: string,
	params: Record<string, unknown>
): Promise<T[]> {
	const rows: T[] = [];
	for (let currentPage = 1; ; currentPage++) {
		const response = await api.get(url, {
			params: { ...params, currentPage, pageSize: 500 },
			paramsSerializer,
		});
		const data = readPage<T>(url, response.data);
		rows.push(...data.items);
		if (currentPage >= data.totalPages) return rows;
	}
}
